/**
 * Same-origin multi-tab bus for the sources picker detach flow.
 *
 * @param {{ sessionId: string, role: 'host' | 'detached' }} options
 * @returns {{
 *   peerId: string,
 *   getSessionId: () => string,
 *   start: () => void,
 *   send: (kind: string, payload?: unknown) => void,
 *   onMessage: (handler: (message: object) => void) => () => void,
 *   destroy: () => void,
 * }}
 */
export function createSourcesPickerBus(options) {
	const sessionId = options?.sessionId;
	const role = options?.role;

	if (!sessionId || (role !== 'host' && role !== 'detached')) {
		throw new Error('sessionId and role (host|detached) are required');
	}

	const peerId = crypto.randomUUID();
	const handlers = new Set();

	/** @type {BroadcastChannel | null} */
	let channel = null;
	let started = false;
	let destroyed = false;

	const kinds = new Set(['SESSION_HELLO', 'DRAG_PACK', 'DRAG_END', 'DETACHED_CLOSED', 'HOST_CLOSED']);

	/**
	 * @param {unknown} data
	 * @returns {boolean}
	 */
	function isValidEnvelope(data) {
		if (!data || typeof data !== 'object') {
			return false;
		}

		const envelope = /** @type {Record<string, unknown>} */ (data);

		if (envelope.sessionId !== sessionId) {
			return false;
		}

		if (typeof envelope.id !== 'string' || envelope.id === '') {
			return false;
		}

		if (envelope.from !== 'host' && envelope.from !== 'detached') {
			return false;
		}

		if (typeof envelope.timestamp !== 'number' || !Number.isFinite(envelope.timestamp)) {
			return false;
		}

		return kinds.has(/** @type {string} */ (envelope.kind));
	}

	/**
	 * @param {unknown} data
	 */
	function receive(data) {
		if (destroyed || !isValidEnvelope(data)) {
			return;
		}

		const envelope = /** @type {Record<string, unknown>} */ (data);

		// Ignore echoes from the same role on this channel.
		if (envelope.from === role) {
			return;
		}

		for (const handler of handlers) {
			try {
				handler(envelope);
			} catch {
				// A listener must not prevent the others from running.
			}
		}
	}

	return {
		peerId,
		getSessionId() {
			return sessionId;
		},
		start() {
			if (destroyed || started) {
				return;
			}

			channel = new BroadcastChannel(`naxos:sources-picker:${sessionId}`);
			channel.onmessage = (event) => {
				receive(event.data);
			};
			started = true;
			this.send('SESSION_HELLO', { role });
		},
		/**
		 * @param {string} kind
		 * @param {unknown} [payload]
		 */
		send(kind, payload) {
			if (destroyed || !started || !channel) {
				return;
			}

			if (!kinds.has(kind)) {
				return;
			}

			/** @type {Record<string, unknown>} */
			const message = {
				kind,
				id: crypto.randomUUID(),
				from: role,
				sessionId,
				timestamp: Date.now(),
			};

			if (payload !== undefined) {
				message.payload = payload;
			}

			channel.postMessage(message);
		},
		/**
		 * @param {(message: object) => void} handler
		 * @returns {() => void}
		 */
		onMessage(handler) {
			handlers.add(handler);
			return () => {
				handlers.delete(handler);
			};
		},
		destroy() {
			if (destroyed) {
				return;
			}

			destroyed = true;
			started = false;

			try {
				channel?.close();
			} catch {
				// Ignore close failures.
			}

			channel = null;
			handlers.clear();
		},
	};
}
